import { Box, Divider, FormHelperText, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import {
  GL_TRANSACTION_DATE_TYPE_LABELS,
  GL_TRANSACTION_POSTED_BY_LABELS,
  GL_TRANSACTION_ROW_TYPE_LABELS,
  GL_TRANSACTION_STATUS_LABELS,
} from "../../constants"
import { GlTransactionDateType } from "../../types/searchParams"
import type { TransactionsSearchParams } from "../../types/searchParams"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosCheckbox, AerosDatePicker, AerosRadioGroup } from "@/components/UiKit"
import { GlTransactionPostedByFilter, GlTransactionRowType, GlTransactionStatus } from "@/graphql/codegen/graphql"

interface TransactionsFilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: () => void
}

interface FilterFormValues {
  transactionType?: GlTransactionRowType
  statuses: GlTransactionStatus[]
  postedByType?: GlTransactionPostedByFilter
  dateType?: GlTransactionDateType
  dateFrom?: string
  dateTo?: string
}

const transactionTypeList = Object.values(GlTransactionRowType)
const filterableStatusList = [
  GlTransactionStatus.Posted,
  GlTransactionStatus.Scheduled,
  GlTransactionStatus.Reversed,
  GlTransactionStatus.Cancelled,
]
const postedByList = Object.values(GlTransactionPostedByFilter)
const dateTypeList = Object.values(GlTransactionDateType)

export const TransactionsFilterDrawer: FC<TransactionsFilterDrawerProps> = ({ showFilterDrawer, toggleDrawer }) => {
  const navigate = useNavigate({ from: "/app/accounting/gl-transactions" })
  const currentSearch: TransactionsSearchParams = useSearch({
    from: "/app/accounting/_layout/gl-transactions",
    strict: true,
  })

  const { transactionType, statuses = [], postedByType, dateType, dateFrom, dateTo } = currentSearch

  const methods = useForm<FilterFormValues>({
    values: {
      transactionType,
      statuses,
      postedByType,
      dateType,
      dateFrom,
      dateTo,
    },
    mode: "onChange",
  })

  const { watch, setValue } = methods

  const watchedDateFrom = watch("dateFrom")
  const watchedDateTo = watch("dateTo")

  const isDateRangeInvalid = !!watchedDateFrom && !!watchedDateTo && watchedDateTo < watchedDateFrom

  const handleClearFilters = async () => {
    methods.reset({
      transactionType: undefined,
      statuses: [],
      postedByType: undefined,
      dateType: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    })
    await navigate({
      search: (prev) => ({
        ...prev,
        transactionType: undefined,
        statuses: undefined,
        postedByType: undefined,
        dateType: undefined,
        dateFrom: undefined,
        dateTo: undefined,
      }),
      replace: true,
    })
  }

  const handleFilterChange = useCallback(
    async (name: keyof TransactionsSearchParams, value: unknown) => {
      await navigate({
        search: (prev: TransactionsSearchParams) => {
          const val = Array.isArray(value) && value.length === 0 ? undefined : value
          return { ...prev, [name]: val as never }
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleTransactionTypeChange = (type: GlTransactionRowType) => {
    const current = watch("transactionType")
    const newType = current === type ? undefined : type
    setValue("transactionType", newType)
    handleFilterChange("transactionType", newType)
  }

  const handleStatusChange = (status: GlTransactionStatus) => {
    const current = watch("statuses") ?? []
    const newStatuses = current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
    setValue("statuses", newStatuses)
    handleFilterChange("statuses", newStatuses)
  }

  const handlePostedByChange = (postedBy: GlTransactionPostedByFilter) => {
    const current = watch("postedByType")
    const newPostedBy = current === postedBy ? undefined : postedBy
    setValue("postedByType", newPostedBy)
    handleFilterChange("postedByType", newPostedBy)
  }

  const handleDateTypeChange = (value: GlTransactionDateType) => {
    setValue("dateType", value)
    handleFilterChange("dateType", value)
  }

  const handleDateFromChange = (value: string | null) => {
    setValue("dateFrom", value ?? undefined)
    const toValue = watch("dateTo")
    const isInvalid = !!value && !!toValue && value > toValue
    if (!isInvalid) {
      handleFilterChange("dateFrom", value ?? undefined)
    }
  }

  const handleDateToChange = (value: string | null) => {
    setValue("dateTo", value ?? undefined)
    const fromValue = watch("dateFrom")
    const isInvalid = !!value && !!fromValue && value < fromValue
    if (!isInvalid) {
      handleFilterChange("dateTo", value ?? undefined)
    }
  }

  return (
    <SearchFilterDrawer toggleDrawer={toggleDrawer} open={showFilterDrawer} clear={handleClearFilters}>
      <FormProvider {...methods}>
        <Box className='h-full min-w-80'>
          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Transaction Type
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              {transactionTypeList.map((type) => (
                <AerosCheckbox
                  key={type}
                  checked={watch("transactionType") === type}
                  onChange={() => handleTransactionTypeChange(type)}
                  label={GL_TRANSACTION_ROW_TYPE_LABELS[type]}
                  className='w-full'
                />
              ))}
            </Box>
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Status
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              {filterableStatusList.map((status) => (
                <AerosCheckbox
                  key={status}
                  checked={watch("statuses")?.includes(status) ?? false}
                  onChange={() => handleStatusChange(status)}
                  label={GL_TRANSACTION_STATUS_LABELS[status]}
                  className='w-full'
                />
              ))}
            </Box>
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Posted By
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              {postedByList.map((postedBy) => (
                <AerosCheckbox
                  key={postedBy}
                  checked={watch("postedByType") === postedBy}
                  onChange={() => handlePostedByChange(postedBy)}
                  label={GL_TRANSACTION_POSTED_BY_LABELS[postedBy]}
                  className='w-full'
                />
              ))}
            </Box>
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Date Range
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              <Box className='flex flex-col gap-2 py-2'>
                <AerosRadioGroup
                  options={dateTypeList.map((dt) => ({ value: dt, label: GL_TRANSACTION_DATE_TYPE_LABELS[dt] }))}
                  value={watch("dateType") ?? ""}
                  onChange={(dt) => handleDateTypeChange(dt)}
                />
              </Box>
              <Box className='flex flex-col gap-4 pb-4'>
                <AerosDatePicker
                  label='From'
                  value={watch("dateFrom") ?? null}
                  onChange={handleDateFromChange}
                  error={isDateRangeInvalid}
                  required
                />
                <AerosDatePicker
                  label='To'
                  value={watch("dateTo") ?? null}
                  onChange={handleDateToChange}
                  error={isDateRangeInvalid}
                  required
                />
                {isDateRangeInvalid && <FormHelperText error>From date must be before To date</FormHelperText>}
              </Box>
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
